import { Search, Filter, X, ArrowUp, ArrowDown, Shield, Plus, CheckSquare, Star, Trash2 } from "lucide-react";
import type { LeadView, LeadViewsPref } from "../utils/leadViews";
import { useState, useRef, useEffect, useMemo } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Label } from "@/app/_module/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/app/_module/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/app/_module/components/ui/accordion";
import { useCustomFieldValues, usePipelineStages } from "../hooks/queries";
import { CustomFieldColumnPicker } from "./CustomFieldColumnPicker";
import { groupCustomFieldOptions, KEY_FIELD_CANONICALS, canonicalCustomFieldLabel, type CustomFieldOption } from "@/app/_module/utils/customFieldAliases";
import { useKeyFieldsOnly } from "@/app/_module/hooks/useKeyFieldsOnly";

const KEY_FIELD_LABELS = KEY_FIELD_CANONICALS.map((c) => canonicalCustomFieldLabel(c) ?? c).join(", ");

// US States list
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// US Timezones with their states
export const US_TIMEZONES = [
  { value: "EST", label: "Eastern (EST/EDT)", abbr: "EST" },
  { value: "CST", label: "Central (CST/CDT)", abbr: "CT" },
  { value: "MST", label: "Mountain (MST/MDT)", abbr: "MT" },
  { value: "PST", label: "Pacific (PST/PDT)", abbr: "PT" },
  { value: "AKST", label: "Alaska (AKST/AKDT)", abbr: "AK" },
  { value: "HST", label: "Hawaii (HST)", abbr: "HI" },
] as const;

// Map US states to their primary timezone
export const STATE_TO_TIMEZONE: Record<string, string> = {
  // Eastern Time
  CT: "EST", DE: "EST", DC: "EST", FL: "EST", GA: "EST",
  IN: "EST", KY: "EST", ME: "EST", MD: "EST", MA: "EST",
  MI: "EST", NH: "EST", NJ: "EST", NY: "EST", NC: "EST",
  OH: "EST", PA: "EST", RI: "EST", SC: "EST", VT: "EST",
  VA: "EST", WV: "EST",
  // Central Time
  AL: "CST", AR: "CST", IL: "CST", IA: "CST", KS: "CST",
  LA: "CST", MN: "CST", MS: "CST", MO: "CST", NE: "CST",
  ND: "CST", OK: "CST", SD: "CST", TN: "CST", TX: "CST",
  WI: "CST",
  // Mountain Time
  AZ: "MST", CO: "MST", ID: "MST", MT: "MST", NM: "MST",
  UT: "MST", WY: "MST",
  // Pacific Time
  CA: "PST", NV: "PST", OR: "PST", WA: "PST",
  // Alaska Time
  AK: "AKST",
  // Hawaii Time
  HI: "HST",
};

/** One custom-field condition: a value set OR a numeric range on that field. */
export interface CustomFieldCondition {
  key: string;
  values?: string[];
  /** Number or date string — the server decides how to compare. */
  min?: string | number | null;
  max?: string | number | null;
}

export interface LeadFilters {
  // Policy filters
  showInactivePoliciesOnly: boolean;
  hasPolicies: boolean | null; // null = any, true = has policies, false = no policies

  // Assignment filters
  assignmentStatus: 'all' | 'assigned' | 'unassigned';

  // Contact filters
  hasPhone: boolean | null;
  hasEmail: boolean | null;

  // Household filters
  householdStatus: 'all' | 'in_household' | 'primary' | 'member' | 'no_household';

  // Date filters
  createdDateRange: 'all' | 'today' | 'this_week' | 'this_month' | 'last_30_days' | 'last_90_days';

  // Location filters
  states: string[]; // Filter by US state(s) — multi-select
  timezone: string[]; // Filter by US timezone(s) — multi-select (EST, CST, MST, PST, AKST, HST)

  // Age filters
  minAge: number | null;
  maxAge: number | null;

  // Gender filter
  gender: 'all' | 'male' | 'female';

  // Call history filters
  hasBeenCalled: boolean | null;
  neverCalled: boolean;
  // "Not called in N days" — includes never-called leads. null = off.
  notCalledInDays: number | null;
  // Called leads whose longest conversation is under N seconds (voicemail doesn't count). null = off.
  talkUnderSeconds: number | null;

  // Lead source filter
  listName: string | null;

  // No Sale Reason filter
  noSaleReason: string | null;

  // City filter
  city: string | null;

  // Assigned to specific agent
  assignedToAgentId: string | null;

  // Carrier filter (from policies)
  carrier: string | null;

  // Tags filter
  tags: string[];

  // Pipeline: stage values (tab values) left out of Show All — "every stage but these".
  // Ignored while a stage tab is picked: the tab shows its own stage.
  hiddenStatuses: string[];

  // Only leads that came from a lead vendor (vendor API drops)
  fromVendor: boolean;

  /**
   * ADDITIONAL custom-field conditions, AND-ed with the one being edited below
   * and with each other — e.g. Carrier=Americo AND Product Type=Whole Life AND
   * Premium>=50. Agents were saving a pipeline per combination because only one
   * field could be filtered at a time; this is that fix.
   */
  customFieldFilters: CustomFieldCondition[];

  // The custom field currently being edited — e.g. Product Type IN (Whole Life, IUL).
  // Applied alongside customFieldFilters, so a single condition needs no "add".
  customFieldKey: string | null;
  customFieldValues: string[];
  // OR a numeric range on the chosen custom field — e.g. Monthly Premium ≥ 50.
  // Mutually exclusive with customFieldValues (whichever is set wins).
  // Number OR date ("01/01/2023", "2023-01-01", or a bare "2023"). Kept as the
  // raw string for dates — coercing to a number turned them into NaN, which is
  // why an Effective Date range could never be expressed.
  customFieldMin: string | number | null;
  customFieldMax: string | number | null;

  // Visible columns
  visibleColumns: string[];
}

/** Every column the Leads table can show. Also drives Settings → Preferences → Tables. */
export const LEAD_TABLE_COLUMNS = [
  { key: 'household', label: 'Household' },
  { key: 'phone', label: 'Phone' },
  { key: 'status', label: 'Status' },
  { key: 'policies', label: 'Policies' },
  { key: 'age', label: 'Age' },
  { key: 'lastCall', label: 'Last Call' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'updatedAt', label: 'Last Updated' },
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'email', label: 'Email' },
  { key: 'dob', label: 'DOB' },
  { key: 'gender', label: 'Gender' },
  { key: 'listName', label: 'List Name' },
  { key: 'zipCode', label: 'Zip Code' },
] as const;
export const DEFAULT_LEAD_COLUMNS: string[] = ['household', 'phone', 'status', 'policies', 'age', 'lastCall', 'createdAt', 'updatedAt'];

export const DEFAULT_FILTERS: LeadFilters = {
  showInactivePoliciesOnly: false,
  hasPolicies: null,
  assignmentStatus: 'all',
  hasPhone: null,
  hasEmail: null,
  householdStatus: 'all',
  createdDateRange: 'all',
  states: [],
  timezone: [],
  minAge: null,
  maxAge: null,
  gender: 'all',
  hasBeenCalled: null,
  neverCalled: false,
  notCalledInDays: null,
  talkUnderSeconds: null,
  listName: null,
  noSaleReason: null,
  city: null,
  assignedToAgentId: null,
  carrier: null,
  tags: [],
  hiddenStatuses: [],
  fromVendor: false,
  customFieldFilters: [],
  customFieldKey: null,
  customFieldValues: [],
  customFieldMin: null,
  customFieldMax: null,
  visibleColumns: DEFAULT_LEAD_COLUMNS,
};

/** Adds the Pipeline filter's hidden stages to a comma-joined `excludeStatuses` value. */
export const withHiddenStages = (exclude: string, hidden: string[] = []) =>
  [...new Set([...exclude.split(',').filter(Boolean), ...hidden])].join(',');

interface LeadsSearchProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedCount: number;
  totalCount: number;
  // Legacy props for backward compatibility
  showInactivePoliciesOnly?: boolean;
  onShowInactivePoliciesChange?: (value: boolean) => void;
  inactivePoliciesCount?: number;
  // New comprehensive filters
  filters?: LeadFilters;
  onFiltersChange?: (filters: LeadFilters) => void;
  // Available list names for lead source filter
  availableListNames?: string[];
  // Available carriers for carrier filter
  availableCarriers?: string[];
  // Available tags from database
  availableTags?: string[];
  // Available agents for agent filter
  availableAgents?: { id: string; name: string }[];
  // Custom-field headers available for the sort: [{ key, type: 'numeric' | 'text' }]
  availableCustomFields?: Array<{ key: string; type: string }>;
  /** The Leads list's own query filters (scope=list): the Values picker lists only the leads on screen. */
  valuesScope?: Record<string, string | undefined>;
  // Sort options
  sortBy?: string;
  onSortChange?: (sortBy: string) => void;
  /** Saved views (filters + sort + columns + page size) with a default. */
  leadViews?: {
    views: LeadViewsPref;
    onApply: (view: LeadView) => void;
    onSave: (name: string) => void;
    onDelete: (id: string) => void;
    onSetDefault: (id: string | null) => void;
  };
  // PolicyGuard
  selectedStatus?: string;
  onOpenPolicyGuard?: () => void;
  policyGuardAlertCount?: number;
}

export const LeadsSearch = function({
  searchTerm,
  onSearchChange,
  selectedCount,
  totalCount,
  showInactivePoliciesOnly = false,
  onShowInactivePoliciesChange,
  inactivePoliciesCount = 0,
  filters: externalFilters,
  onFiltersChange,
  availableListNames = [],
  availableTags = [],
  availableCustomFields = [],
  valuesScope,
  sortBy = 'lastContacted',
  onSortChange,
  leadViews,
  selectedStatus,
  onOpenPolicyGuard,
  policyGuardAlertCount = 0,
}: LeadsSearchProps) {
  // Use internal state if no external filters provided
  const [internalFilters, setInternalFilters] = useState<LeadFilters>({
    ...DEFAULT_FILTERS,
    showInactivePoliciesOnly,
  });

  const filters = externalFilters || internalFilters;
  const setFilters = onFiltersChange || setInternalFilters;
  const [newViewName, setNewViewName] = useState("");

  // Distinct values of the selected custom field among the leads on screen. The field's own condition
  // is left out (the server does the same), or each tick would re-fetch and hide the other values.
  const pickerScope = useMemo(() => {
    if (!valuesScope) return undefined;
    const { customFieldKey: _k, customFieldValues: _v, customFieldMin: _min, customFieldMax: _max, ...rest } = valuesScope;
    const own = (filters.customFieldKey ?? '').trim().toLowerCase();
    const others = filters.customFieldFilters.filter((c) => c.key.trim().toLowerCase() !== own);
    return { ...rest, customFieldFilters: others.length ? JSON.stringify(others) : undefined };
  }, [valuesScope, filters.customFieldKey, filters.customFieldFilters]);
  const { data: customFieldValues = [] } = useCustomFieldValues(filters.customFieldKey, pickerScope);

  // Pipeline filter options: the stage tabs, same values and names as StatusCards.
  const { data: pipelineStages = [] } = usePipelineStages();
  const stageOptions = useMemo(
    () =>
      pipelineStages
        .filter((s) => s.name.toLowerCase() !== 'disposed')
        .map((s) => ({ value: s.systemValue || s.name, label: s.systemValue === 'New lead' ? 'Leads' : s.name, color: s.color })),
    [pipelineStages],
  );
  const hiddenStatuses = filters.hiddenStatuses ?? [];

  // Agency-wide field list, grouped (synonyms/typos merged, internal `_` keys
  // dropped, key fields first) and narrowed to the three key fields by default.
  // Brandon (Duse Agency): 610 distinct keys; agents only ever want Carrier /
  // Annual Premium / Monthly Premium. Untick "Key fields only" to see the rest;
  // typing in the search box always searches everything.
  const groupedFields = useMemo(() => groupCustomFieldOptions(availableCustomFields), [availableCustomFields]);
  const keyFields = useMemo(() => groupedFields.filter((f) => f.isKeyField), [groupedFields]);
  const [keyFieldsOnly, setKeyFieldsOnly] = useKeyFieldsOnly();
  const keyOnlyActive = keyFieldsOnly && keyFields.length > 0;
  const pickerFields: CustomFieldOption[] = keyOnlyActive ? keyFields : groupedFields;
  // Values vs Range is a UI mode (local) so switching to Range opens with EMPTY
  // inputs instead of pre-filling 0 (which forced users to delete the 0 first).
  const [cfMode, setCfMode] = useState<'values' | 'range'>(
    filters.customFieldMin != null || filters.customFieldMax != null ? 'range' : 'values',
  );
  const cfRangeMode = cfMode === 'range';

  // Raw text for the min/max inputs. Kept separate from the numeric filter so
  // decimals type cleanly (a controlled number input strips the trailing "." of
  // "0.12" as you type). Only clears the text when the filter is emptied
  // externally (clear-all / field change) — never mid-typing.
  const [cfMinStr, setCfMinStr] = useState(filters.customFieldMin != null ? String(filters.customFieldMin) : '');
  const [cfMaxStr, setCfMaxStr] = useState(filters.customFieldMax != null ? String(filters.customFieldMax) : '');
  useEffect(() => { if (filters.customFieldMin == null) setCfMinStr(''); }, [filters.customFieldMin]);
  useEffect(() => { if (filters.customFieldMax == null) setCfMaxStr(''); }, [filters.customFieldMax]);

  // Search boxes for the custom-field picker and its value list — some agencies
  // have dozens of custom fields / hundreds of values, too many to scroll.
  const [cfFieldSearch, setCfFieldSearch] = useState('');
  const [cfValueSearch, setCfValueSearch] = useState('');
  useEffect(() => { setCfValueSearch(''); }, [filters.customFieldKey]);

  const handleFilterChange = <K extends keyof LeadFilters>(key: K, value: LeadFilters[K]) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Backward compatibility: also call legacy handler for showInactivePoliciesOnly
    if (key === 'showInactivePoliciesOnly' && onShowInactivePoliciesChange) {
      onShowInactivePoliciesChange(value as boolean);
    }
  };

  const clearAllFilters = () => {
    // Columns are a layout choice, not a filter — keep them.
    setFilters({ ...DEFAULT_FILTERS, visibleColumns: filters.visibleColumns });
    if (onShowInactivePoliciesChange) {
      onShowInactivePoliciesChange(false);
    }
  };

  // Count active filters
  const activeFiltersCount = [
    filters.hasPolicies !== null,
    filters.assignmentStatus !== 'all',
    filters.hasPhone !== null,
    filters.hasEmail !== null,
    filters.householdStatus !== 'all',
    filters.createdDateRange !== 'all',
    filters.states.length > 0,
    filters.timezone.length > 0,
    filters.minAge !== null,
    filters.maxAge !== null,
    filters.gender !== 'all',
    filters.hasBeenCalled !== null,
    filters.neverCalled,
    filters.notCalledInDays !== null,
    filters.talkUnderSeconds != null,
    filters.listName !== null,
    filters.city !== null,
    filters.tags.length > 0,
    hiddenStatuses.length > 0,
    filters.fromVendor,
    !!(filters.customFieldKey && (filters.customFieldValues.length > 0 || filters.customFieldMin != null || filters.customFieldMax != null)),
    filters.customFieldFilters.length > 0,
  ].filter(Boolean).length;

  // Get active filter badges
  const getActiveFilterBadges = () => {
    // `customFieldFilters:<index>` targets one condition in the list, so the
    // key is not always a LeadFilters field name.
    const badges: { label: string; key: keyof LeadFilters | `customFieldFilters:${number}` }[] = [];

    if (filters.hasPolicies === true) badges.push({ label: 'Has Policies', key: 'hasPolicies' });
    if (filters.hasPolicies === false) badges.push({ label: 'No Policies', key: 'hasPolicies' });
    if (filters.assignmentStatus === 'assigned') badges.push({ label: 'Assigned', key: 'assignmentStatus' });
    if (filters.assignmentStatus === 'unassigned') badges.push({ label: 'Unassigned', key: 'assignmentStatus' });
    if (filters.hasPhone === true) badges.push({ label: 'Has Phone', key: 'hasPhone' });
    if (filters.hasPhone === false) badges.push({ label: 'Missing Phone', key: 'hasPhone' });
    if (filters.hasEmail === true) badges.push({ label: 'Has Email', key: 'hasEmail' });
    if (filters.hasEmail === false) badges.push({ label: 'Missing Email', key: 'hasEmail' });
    if (filters.householdStatus === 'in_household') badges.push({ label: 'In Household', key: 'householdStatus' });
    if (filters.householdStatus === 'primary') badges.push({ label: 'Primary Lead', key: 'householdStatus' });
    if (filters.householdStatus === 'member') badges.push({ label: 'Household Member', key: 'householdStatus' });
    if (filters.householdStatus === 'no_household') badges.push({ label: 'No Household', key: 'householdStatus' });
    if (filters.createdDateRange !== 'all') {
      const labels: Record<string, string> = {
        today: 'Created Today',
        this_week: 'Created This Week',
        this_month: 'Created This Month',
        last_30_days: 'Last 30 Days',
        last_90_days: 'Last 90 Days',
      };
      badges.push({ label: labels[filters.createdDateRange] || filters.createdDateRange, key: 'createdDateRange' });
    }
    if (filters.states.length > 0) {
      const stateLabels = filters.states.map(s => US_STATES.find(u => u.value === s)?.label || s);
      badges.push({ label: `States: ${stateLabels.join(', ')}`, key: 'states' });
    }
    if (filters.timezone.length > 0) {
      const tzLabels = filters.timezone.map(tz => {
        const found = US_TIMEZONES.find(t => t.value === tz);
        return found?.abbr || tz;
      });
      badges.push({ label: `TZ: ${tzLabels.join(', ')}`, key: 'timezone' });
    }
    if (filters.minAge !== null || filters.maxAge !== null) {
      const ageLabel = filters.minAge !== null && filters.maxAge !== null
        ? `Age ${filters.minAge}-${filters.maxAge}`
        : filters.minAge !== null
        ? `Age ${filters.minAge}+`
        : `Age under ${filters.maxAge}`;
      badges.push({ label: ageLabel, key: 'minAge' });
    }
    if (filters.gender !== 'all') badges.push({ label: filters.gender === 'male' ? 'Male' : 'Female', key: 'gender' });
    if (filters.hasBeenCalled === true) badges.push({ label: 'Has Been Called', key: 'hasBeenCalled' });
    if (filters.neverCalled) badges.push({ label: 'Never Called', key: 'neverCalled' });
    if (filters.notCalledInDays !== null) badges.push({ label: `Not called in ${filters.notCalledInDays}d`, key: 'notCalledInDays' });
    if (filters.talkUnderSeconds != null) badges.push({ label: `Never talked ${filters.talkUnderSeconds}s+`, key: 'talkUnderSeconds' });
    if (filters.listName) {
      const sourceLabel = filters.listName === '__none__' ? 'Manual / No Source' : filters.listName;
      badges.push({ label: `Source: ${sourceLabel}`, key: 'listName' });
    }
    if (filters.city) {
      badges.push({ label: `City: ${filters.city}`, key: 'city' });
    }
    if (filters.tags.length > 0) {
      badges.push({ label: `Tags: ${filters.tags.map(t => t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ')}`, key: 'tags' });
    }
    if (hiddenStatuses.length > 0) {
      const names = hiddenStatuses.map((v) => stageOptions.find((s) => s.value === v)?.label ?? v);
      badges.push({ label: `Hiding: ${names.join(', ')}`, key: 'hiddenStatuses' });
    }
    if (filters.fromVendor) {
      badges.push({ label: 'From Lead Vendor', key: 'fromVendor' });
    }
    filters.customFieldFilters.forEach((c, i) => {
      const label = c.values?.length
        ? `${c.key}: ${c.values.slice(0, 2).join(', ')}${c.values.length > 2 ? ` +${c.values.length - 2}` : ''}`
        : `${c.key}: ${c.min != null ? `≥${c.min}` : ''}${c.min != null && c.max != null ? ' ' : ''}${c.max != null ? `≤${c.max}` : ''}`;
      badges.push({ label, key: `customFieldFilters:${i}` });
    });
    if (filters.customFieldKey && (filters.customFieldMin != null || filters.customFieldMax != null)) {
      const { customFieldMin: mn, customFieldMax: mx } = filters;
      const rangeLabel = mn != null && mx != null ? `${mn}–${mx}` : mn != null ? `≥ ${mn}` : `≤ ${mx}`;
      badges.push({ label: `${filters.customFieldKey}: ${rangeLabel}`, key: 'customFieldKey' });
    } else if (filters.customFieldKey && filters.customFieldValues.length > 0) {
      const vals = filters.customFieldValues;
      const shown = vals.length <= 2 ? vals.join(', ') : `${vals.slice(0, 2).join(', ')} +${vals.length - 2}`;
      badges.push({ label: `${filters.customFieldKey}: ${shown}`, key: 'customFieldKey' });
    }

    return badges;
  };

  const removeBadge = (key: keyof LeadFilters | `customFieldFilters:${number}`) => {
    const resetValues: Partial<LeadFilters> = {
      showInactivePoliciesOnly: false,
      hasPolicies: null,
      assignmentStatus: 'all',
      hasPhone: null,
      hasEmail: null,
      householdStatus: 'all',
      createdDateRange: 'all',
      states: [],
      timezone: [],
      minAge: null,
      maxAge: null,
      gender: 'all',
      hasBeenCalled: null,
      neverCalled: false,
      notCalledInDays: null,
      talkUnderSeconds: null,
      listName: null,
      noSaleReason: null,
      tags: [],
      hiddenStatuses: [],
      fromVendor: false,
    };

    // Special case: reset both min and max in a single setFilters call
    // (calling handleFilterChange twice uses stale `filters` both times)
    if (key === 'minAge' || key === 'maxAge') {
      setFilters({ ...filters, minAge: null, maxAge: null });
    } else if (key.startsWith('customFieldFilters:')) {
      const idx = Number(key.split(':')[1]);
      setFilters({ ...filters, customFieldFilters: filters.customFieldFilters.filter((_, j) => j !== idx) });
    } else if (key === 'customFieldKey') {
      // Removing the custom-field badge clears the chosen field, its values AND range
      setFilters({ ...filters, customFieldKey: null, customFieldValues: [], customFieldMin: null, customFieldMax: null });
    } else {
      // Narrowed by the branches above — anything reaching here is a real
      // LeadFilters key, not an indexed custom-field condition.
      const k = key as keyof LeadFilters;
      handleFilterChange(k, resetValues[k] as any);
    }
  };

  return (
    <div data-tour="leads-filters" className="p-6 border-b border-border">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div data-tour="leads-search" className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                className="pl-10 border-muted transition-all duration-200 focus:border-primary"
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Find a phone number or lead name..."
                value={searchTerm}
              />
            </div>

            {/* Filter Dropdown - modal={false} allows nested Select components to work */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="relative gap-2"
                  size="sm"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                    >
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="leads-filter-menu w-64 p-2 max-h-[400px] overflow-y-auto overflow-x-hidden" onCloseAutoFocus={(e) => e.preventDefault()}>
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm text-foreground">Filter Options</h4>
                    {activeFiltersCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAllFilters}
                        className="text-xs h-7 px-2 text-muted-foreground hover:text-destructive"
                      >
                        Clear all
                      </Button>
                    )}
                  </div>

                  <Accordion type="multiple" className="w-full" defaultValue={["views", "source", "location"]}>
                    {/* Saved views — the whole page (filters, sort, columns, page size) under a name */}
                    {leadViews && (
                      <AccordionItem value="views" className="border-b">
                        <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                          <span className="flex items-center gap-1">Views {leadViews.views.items.length > 0 && <span className="text-xs text-primary">({leadViews.views.items.length})</span>}</span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-1.5 pb-3">
                          {leadViews.views.items.map((v) => {
                            const isDefault = leadViews.views.defaultId === v.id;
                            return (
                              <div key={v.id} className="group flex items-center gap-1 rounded px-1 py-0.5 hover:bg-muted/50">
                                <button type="button" onClick={() => leadViews.onApply(v)} className="flex-1 truncate text-left text-sm hover:text-primary" title="Apply this view">
                                  {v.name}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => leadViews.onSetDefault(isDefault ? null : v.id)}
                                  title={isDefault ? "Default view — the page opens like this. Click to unset." : "Make this the default view"}
                                  className={isDefault ? "text-amber-500" : "text-gray-300 hover:text-amber-500"}
                                >
                                  <Star className="h-3.5 w-3.5" fill={isDefault ? "currentColor" : "none"} />
                                </button>
                                <button type="button" onClick={() => leadViews.onDelete(v.id)} title="Delete view" className="text-gray-300 hover:text-red-500">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            );
                          })}
                          <form
                            className="flex items-center gap-1.5 pt-1"
                            onSubmit={(e) => {
                              e.preventDefault();
                              const name = newViewName.trim();
                              if (!name) return;
                              leadViews.onSave(name);
                              setNewViewName("");
                            }}
                          >
                            <input
                              value={newViewName}
                              onChange={(e) => setNewViewName(e.target.value)}
                              placeholder="Save current page as…"
                              className="h-7 flex-1 rounded-md border border-dashed border-gray-300 bg-transparent px-2 text-xs placeholder:text-gray-400 focus:border-primary focus:outline-none"
                            />
                            <button type="submit" disabled={!newViewName.trim()} className="text-xs font-medium text-primary disabled:text-gray-300">
                              Save
                            </button>
                          </form>
                          <p className="text-[10px] text-gray-400">Saves filters, sort, columns and page size. The starred view opens by default.</p>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                    {/* Pipeline: Show All minus the checked stages */}
                    {stageOptions.length > 0 && (
                      <AccordionItem value="pipeline" className="border-b">
                        <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                          <span className="flex items-center gap-1">Pipeline {hiddenStatuses.length > 0 && <span className="text-xs text-primary">({hiddenStatuses.length} hidden)</span>}</span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-1.5 pb-3">
                          <p className="text-xs text-muted-foreground">Hide these stages in Show All:</p>
                          {stageOptions.map((stage) => (
                            <label key={stage.value} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={hiddenStatuses.includes(stage.value)}
                                onCheckedChange={(checked) =>
                                  handleFilterChange(
                                    'hiddenStatuses',
                                    checked === true ? [...hiddenStatuses, stage.value] : hiddenStatuses.filter((v) => v !== stage.value),
                                  )
                                }
                              />
                              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: stage.color || '#9ca3af' }} />
                              <span className="truncate text-xs">{stage.label}</span>
                            </label>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Lead Source Filter */}
                    <AccordionItem value="source" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Lead Source
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Source / List Name</Label>
                          <Select
                            value={filters.listName || 'all'}
                            onValueChange={(value) => handleFilterChange('listName', value === 'all' ? null : value)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="All sources" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              <SelectItem value="all">All sources</SelectItem>
                              <SelectItem value="__none__">Manual / No source</SelectItem>
                              {availableListNames.filter((n) => typeof n === "string" && n.trim() !== "").map((name) => (
                                <SelectItem key={name} value={name}>
                                  {name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* From lead vendor only */}
                        <label className="flex items-center gap-2 cursor-pointer pt-1">
                          <Checkbox
                            checked={filters.fromVendor}
                            onCheckedChange={(checked) => handleFilterChange('fromVendor', checked === true)}
                          />
                          <span className="text-xs text-muted-foreground">From lead vendor only</span>
                        </label>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Tags Filter */}
                    <AccordionItem value="tags" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        <span className="flex items-center gap-1">Tags {filters.tags.length > 0 && <span className="text-xs text-primary">({filters.tags.length})</span>}</span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-3">
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {(() => {
                            const presets = [
                              { value: "hot_lead", label: "Hot Lead", color: "bg-red-50 text-red-700 border-red-200" },
                              { value: "interested", label: "Interested", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                              { value: "callback", label: "Callback", color: "bg-amber-50 text-amber-700 border-amber-200" },
                              { value: "not_home", label: "Not Home", color: "bg-blue-50 text-blue-700 border-blue-200" },
                              { value: "referral", label: "Referral", color: "bg-violet-50 text-violet-700 border-violet-200" },
                              { value: "follow_up", label: "Follow Up", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
                              { value: "spanish", label: "Spanish", color: "bg-orange-50 text-orange-700 border-orange-200" },
                              { value: "dog", label: "Dog", color: "bg-orange-50 text-orange-600 border-orange-200" },
                              { value: "gated", label: "Gated", color: "bg-gray-100 text-gray-600 border-gray-200" },
                              { value: "no_soliciting", label: "No Soliciting", color: "bg-red-50 text-red-600 border-red-200" },
                            ];
                            const presetValues = new Set(presets.map(p => p.value));
                            const customFromDb = availableTags
                              .filter(t => !presetValues.has(t))
                              .map(t => ({
                                value: t,
                                label: t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                                color: "bg-gray-100 text-gray-700 border-gray-200",
                              }));
                            return [...presets, ...customFromDb];
                          })().map(tag => {
                            const isSelected = filters.tags.includes(tag.value);
                            return (
                              <button
                                key={tag.value}
                                type="button"
                                onClick={() => {
                                  const newTags = isSelected
                                    ? filters.tags.filter(t => t !== tag.value)
                                    : [...filters.tags, tag.value];
                                  handleFilterChange('tags', newTags);
                                }}
                                className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-all ${
                                  isSelected
                                    ? `${tag.color} ring-2 ring-primary/30`
                                    : `${tag.color} opacity-60 hover:opacity-100`
                                }`}
                              >
                                {tag.label}
                              </button>
                            );
                          })}
                        </div>
                        {/* Custom tag filter input */}
                        <FilterCustomTagInput onAddTag={(tag) => {
                          if (!filters.tags.includes(tag)) {
                            handleFilterChange('tags', [...filters.tags, tag]);
                          }
                        }} />
                      </AccordionContent>
                    </AccordionItem>

                    {/* Custom Field value filter — pick a field, then check the values to keep */}
                    {availableCustomFields.length > 0 && (
                      <AccordionItem value="customField" className="border-b">
                        <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                          <span className="flex items-center gap-1">
                            Custom Field
                            {filters.customFieldKey && filters.customFieldValues.length > 0 && (
                              <span className="text-xs text-primary">({filters.customFieldValues.length})</span>
                            )}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-2 pb-3">
                          {/* Conditions already added. Each is AND-ed with the
                              others and with the field being edited below, so
                              Carrier + Product Type + Premium compose into one
                              filter instead of needing a saved pipeline each. */}
                          {filters.customFieldFilters.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 rounded-md border bg-muted/30 p-2">
                              {filters.customFieldFilters.map((c, i) => {
                                const label =
                                  c.values?.length
                                    ? `${c.key}: ${c.values.slice(0, 2).join(', ')}${c.values.length > 2 ? ` +${c.values.length - 2}` : ''}`
                                    : `${c.key}: ${c.min != null ? `≥${c.min}` : ''}${c.min != null && c.max != null ? ' ' : ''}${c.max != null ? `≤${c.max}` : ''}`;
                                return (
                                  <span key={`${c.key}-${i}`} className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                    {label}
                                    <button
                                      type="button"
                                      aria-label={`Remove ${c.key} filter`}
                                      onClick={() => setFilters({
                                        ...filters,
                                        customFieldFilters: filters.customFieldFilters.filter((_, j) => j !== i),
                                      })}
                                      className="hover:text-primary/70"
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {keyFields.length > 0 && (
                            <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
                              <Checkbox
                                checked={keyOnlyActive}
                                onCheckedChange={(v) => setKeyFieldsOnly(v === true)}
                                className="mt-0.5"
                              />
                              <span>
                                Key fields only
                                <span className="block text-[11px] text-muted-foreground">
                                  {keyOnlyActive ? KEY_FIELD_LABELS : `All ${groupedFields.length} fields`}
                                </span>
                              </span>
                            </label>
                          )}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Field</Label>
                            <Select
                              value={filters.customFieldKey || ''}
                              onValueChange={(k) => { setFilters({ ...filters, customFieldKey: k || null, customFieldValues: [], customFieldMin: null, customFieldMax: null }); setCfFieldSearch(''); }}
                              onOpenChange={(open) => { if (!open) setCfFieldSearch(''); }}
                            >
                              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select a field…" /></SelectTrigger>
                              <SelectContent className="max-h-60">
                                {groupedFields.length > 8 && (
                                  <div
                                    // -mx-1 / -mt-1 swallow the Viewport's p-1 so items can't peek
                                    // out above or beside the box while the list scrolls under it.
                                    className="sticky top-0 z-10 -mx-1 -mt-1 mb-1 bg-popover p-1 border-b shadow-[0_3px_6px_-2px_rgba(0,0,0,0.12)]"
                                    // Keep keystrokes/clicks from triggering the Select's own
                                    // typeahead + item focus while typing in the search box
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onPointerDown={(e) => e.stopPropagation()}
                                  >
                                    <Input
                                      value={cfFieldSearch}
                                      onChange={(e) => setCfFieldSearch(e.target.value)}
                                      placeholder="Search fields…"
                                      className="h-7 text-xs"
                                    />
                                  </div>
                                )}
                                {(() => {
                                  const q = cfFieldSearch.trim().toLowerCase();
                                  // A typed search looks through EVERY field, not just the key ones.
                                  const pool = q ? groupedFields : pickerFields;
                                  const chosen = filters.customFieldKey;
                                  const fields = pool
                                    .filter((f) => !q || f.key.toLowerCase().includes(q))
                                    // keep the currently chosen field visible even if the list is narrowed
                                    .concat(chosen && !pool.some((f) => f.key === chosen) && !q ? [{ key: chosen, type: 'text', isKeyField: false }] : []);
                                  if (fields.length === 0) {
                                    return <p className="px-2 py-1.5 text-xs text-gray-400">No fields match “{cfFieldSearch}”</p>;
                                  }
                                  return fields.map((f) => (
                                    <SelectItem key={f.key} value={f.key} className="text-sm">{f.key}</SelectItem>
                                  ));
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                          {filters.customFieldKey && (
                            <div className="space-y-2">
                              {/* Values vs Range toggle */}
                              <div className="flex items-center gap-1 rounded-md border bg-gray-50 p-0.5 w-fit">
                                <button
                                  type="button"
                                  onClick={() => { setCfMode('values'); setFilters({ ...filters, customFieldMin: null, customFieldMax: null }); }}
                                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${cfRangeMode ? "text-gray-500 hover:text-gray-900" : "bg-white shadow-sm text-gray-900"}`}
                                >
                                  Values
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setCfMode('range'); setFilters({ ...filters, customFieldValues: [] }); }}
                                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${cfRangeMode ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-900"}`}
                                >
                                  Range
                                </button>
                              </div>

                              {cfRangeMode ? (
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Min (≥)</Label>
                                      <Input
                                        type="text"
                                        inputMode="text"
                                        placeholder="Any"
                                        value={cfMinStr}
                                        onChange={(e) => {
                                          const raw = e.target.value;
                                          // digits, one dot, and date separators — dates are valid bounds
                                          if (raw !== '' && !/^[-\d./]*$/.test(raw)) return;
                                          setCfMinStr(raw);
                                          // Send the raw string; the server decides numeric vs date.
                                          handleFilterChange('customFieldMin', raw.trim() === '' ? null : raw.trim());
                                        }}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <span className="text-gray-400 mt-4">–</span>
                                    <div className="flex-1 space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Max (≤)</Label>
                                      <Input
                                        type="text"
                                        inputMode="text"
                                        placeholder="Any"
                                        value={cfMaxStr}
                                        onChange={(e) => {
                                          const raw = e.target.value;
                                          // digits, one dot, and date separators — dates are valid bounds
                                          if (raw !== '' && !/^[-\d./]*$/.test(raw)) return;
                                          setCfMaxStr(raw);
                                          // Send the raw string; the server decides numeric vs date.
                                          handleFilterChange('customFieldMax', raw.trim() === '' ? null : raw.trim());
                                        }}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                  </div>
                                  <p className="text-[10px] text-gray-400">Numbers (50) or dates (01/01/2023, or just 2023). Leave a side blank for open-ended (Min only = greater than, Max only = less than).</p>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-xs text-muted-foreground">Values</Label>
                                    {filters.customFieldValues.length > 0 && (
                                      <button
                                        onClick={() => handleFilterChange('customFieldValues', [])}
                                        className="text-[10px] text-gray-400 hover:text-gray-600"
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                  {customFieldValues.length > 10 && (
                                    <Input
                                      value={cfValueSearch}
                                      onChange={(e) => setCfValueSearch(e.target.value)}
                                      placeholder={`Search ${customFieldValues.length} values…`}
                                      className="h-7 text-xs"
                                    />
                                  )}
                                  {customFieldValues.length === 0 ? (
                                    <p className="text-xs text-gray-400 py-1">No values found for this field.</p>
                                  ) : (() => {
                                    const vq = cfValueSearch.trim().toLowerCase();
                                    const visibleValues = customFieldValues.filter((v) => !vq || String(v).toLowerCase().includes(vq));
                                    if (visibleValues.length === 0) {
                                      return <p className="text-xs text-gray-400 py-1">No values match “{cfValueSearch}”</p>;
                                    }
                                    return (
                                      <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                                        {visibleValues.map((v) => (
                                          <label key={v} className="flex items-center gap-2 cursor-pointer">
                                            <Checkbox
                                              checked={filters.customFieldValues.includes(v)}
                                              onCheckedChange={(checked) => {
                                                const next = checked === true
                                                  ? [...filters.customFieldValues, v]
                                                  : filters.customFieldValues.filter((x) => x !== v);
                                                handleFilterChange('customFieldValues', next);
                                              }}
                                            />
                                            <span className="text-sm truncate">{v}</span>
                                          </label>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          )}
                              {/* Commit this condition and clear the editor so
                                  another field can be added. The editor's own
                                  value stays applied even without pressing this,
                                  so a single-field filter behaves as before. */}
                              <button
                                type="button"
                                disabled={
                                  !filters.customFieldKey ||
                                  (filters.customFieldValues.length === 0 &&
                                    filters.customFieldMin == null &&
                                    filters.customFieldMax == null)
                                }
                                onClick={() => {
                                  if (!filters.customFieldKey) return;
                                  const condition: CustomFieldCondition = {
                                    key: filters.customFieldKey,
                                    ...(filters.customFieldValues.length
                                      ? { values: filters.customFieldValues }
                                      : {}),
                                    ...(filters.customFieldMin != null ? { min: filters.customFieldMin } : {}),
                                    ...(filters.customFieldMax != null ? { max: filters.customFieldMax } : {}),
                                  };
                                  setFilters({
                                    ...filters,
                                    customFieldFilters: [...filters.customFieldFilters, condition].slice(0, 10),
                                    customFieldKey: null,
                                    customFieldValues: [],
                                    customFieldMin: null,
                                    customFieldMax: null,
                                  });
                                  setCfMinStr('');
                                  setCfMaxStr('');
                                  setCfMode('values');
                                }}
                                className="mt-1 w-full rounded-md border border-dashed py-1.5 text-xs font-medium text-primary hover:bg-primary/5 disabled:opacity-40 disabled:hover:bg-transparent"
                              >
                                + Add another field
                              </button>
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Location Filters */}
                    <AccordionItem value="location" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Location
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            Timezone{filters.timezone.length > 0 ? ` (${filters.timezone.length} selected)` : ''}
                          </Label>
                          <div className="space-y-1.5">
                            {US_TIMEZONES.map((tz) => {
                              const isChecked = filters.timezone.includes(tz.value);
                              return (
                                <label
                                  key={tz.value}
                                  className="flex items-center gap-2 cursor-pointer rounded px-1 py-0.5 hover:bg-muted/50"
                                >
                                  <Checkbox
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      const newTimezones = checked
                                        ? [...filters.timezone, tz.value]
                                        : filters.timezone.filter((t: string) => t !== tz.value);
                                      setFilters({
                                        ...filters,
                                        timezone: newTimezones,
                                        // Clear state filter when any timezone is selected
                                        states: newTimezones.length > 0 ? [] : filters.states,
                                      });
                                    }}
                                  />
                                  <span className="text-sm">{tz.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            State{filters.states.length > 0 ? ` (${filters.states.length} selected)` : ''}
                          </Label>
                          <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-2 bg-background">
                            {US_STATES.map((state) => {
                              const isChecked = filters.states.includes(state.value);
                              return (
                                <label
                                  key={state.value}
                                  className="flex items-center gap-2 cursor-pointer rounded px-1 py-0.5 hover:bg-muted/50"
                                >
                                  <Checkbox
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      const newStates = checked
                                        ? [...filters.states, state.value]
                                        : filters.states.filter((s: string) => s !== state.value);
                                      setFilters({
                                        ...filters,
                                        states: newStates,
                                        // Clear timezone filter when specific states are selected
                                        timezone: newStates.length > 0 ? [] : filters.timezone,
                                      });
                                    }}
                                  />
                                  <span className="text-sm">{state.label}</span>
                                </label>
                              );
                            })}
                          </div>
                          {filters.states.length > 0 && (
                            <button
                              type="button"
                              className="text-xs text-muted-foreground hover:text-foreground underline"
                              onClick={() => handleFilterChange('states', [])}
                            >
                              Clear selected states
                            </button>
                          )}
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">City</Label>
                          <Input
                            type="text"
                            placeholder="Filter by city..."
                            className="h-8 text-sm"
                            value={filters.city || ''}
                            onChange={(e) => handleFilterChange('city', e.target.value || null)}
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Contact Filters */}
                    <AccordionItem value="contact" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Contact Info
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Phone Number</Label>
                          <Select
                            value={filters.hasPhone === null ? 'any' : filters.hasPhone ? 'yes' : 'no'}
                            onValueChange={(value) => handleFilterChange('hasPhone', value === 'any' ? null : value === 'yes')}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any</SelectItem>
                              <SelectItem value="yes">Has phone</SelectItem>
                              <SelectItem value="no">Missing phone</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Email Address</Label>
                          <Select
                            value={filters.hasEmail === null ? 'any' : filters.hasEmail ? 'yes' : 'no'}
                            onValueChange={(value) => handleFilterChange('hasEmail', value === 'any' ? null : value === 'yes')}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any</SelectItem>
                              <SelectItem value="yes">Has email</SelectItem>
                              <SelectItem value="no">Missing email</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Age Range Filter */}
                    <AccordionItem value="age" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Age Range
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Age between</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              placeholder="Min"
                              className="h-8 text-sm w-20"
                              value={filters.minAge || ''}
                              onChange={(e) => handleFilterChange('minAge', e.target.value ? parseInt(e.target.value) : null)}
                              min={0}
                              max={120}
                            />
                            <span className="text-muted-foreground text-sm">to</span>
                            <Input
                              type="number"
                              placeholder="Max"
                              className="h-8 text-sm w-20"
                              value={filters.maxAge || ''}
                              onChange={(e) => handleFilterChange('maxAge', e.target.value ? parseInt(e.target.value) : null)}
                              min={0}
                              max={120}
                            />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Household Filters */}
                    <AccordionItem value="household" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Household
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Household Status</Label>
                          <Select
                            value={filters.householdStatus}
                            onValueChange={(value) => handleFilterChange('householdStatus', value as any)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All leads</SelectItem>
                              <SelectItem value="in_household">In a household</SelectItem>
                              <SelectItem value="primary">Primary leads only</SelectItem>
                              <SelectItem value="member">Household members only</SelectItem>
                              <SelectItem value="no_household">No household</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Date Filters */}
                    <AccordionItem value="dates" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Date Created
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Created Date</Label>
                          <Select
                            value={filters.createdDateRange}
                            onValueChange={(value) => handleFilterChange('createdDateRange', value as any)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="All time" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All time</SelectItem>
                              <SelectItem value="today">Today</SelectItem>
                              <SelectItem value="this_week">This week</SelectItem>
                              <SelectItem value="this_month">This month</SelectItem>
                              <SelectItem value="last_30_days">Last 30 days</SelectItem>
                              <SelectItem value="last_90_days">Last 90 days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Call History Filters */}
                    <AccordionItem value="calls" className="border-b">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Call History
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="never-called"
                            checked={filters.neverCalled}
                            onCheckedChange={(checked) => {
                              // Set both values in one update to avoid stale state
                              const newFilters = {
                                ...filters,
                                neverCalled: checked as boolean,
                                ...(checked ? { hasBeenCalled: null as boolean | null, talkUnderSeconds: null } : {}),
                              };
                              setFilters(newFilters);
                            }}
                          />
                          <Label htmlFor="never-called" className="text-sm cursor-pointer">
                            Never been called
                          </Label>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Call Status</Label>
                          <Select
                            value={filters.hasBeenCalled === null ? 'any' : filters.hasBeenCalled ? 'yes' : 'no'}
                            onValueChange={(value) => {
                              // Set both values in one update to avoid stale state
                              const newFilters = {
                                ...filters,
                                hasBeenCalled: value === 'any' ? null : value === 'yes',
                                ...(value !== 'any' ? { neverCalled: false } : {}),
                                ...(value === 'no' ? { talkUnderSeconds: null } : {}),
                              };
                              setFilters(newFilters);
                            }}
                            disabled={filters.neverCalled}
                          >
                            <SelectTrigger className={`h-8 text-sm ${filters.neverCalled ? 'opacity-50' : ''}`}>
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any</SelectItem>
                              <SelectItem value="yes">Has been called</SelectItem>
                              <SelectItem value="no">Never called</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Not called in</Label>
                          <Select
                            value={filters.notCalledInDays === null ? 'any' : String(filters.notCalledInDays)}
                            onValueChange={(value) => {
                              handleFilterChange('notCalledInDays', value === 'any' ? null : parseInt(value, 10));
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any</SelectItem>
                              <SelectItem value="3">3+ days</SelectItem>
                              <SelectItem value="7">7+ days</SelectItem>
                              <SelectItem value="14">14+ days</SelectItem>
                              <SelectItem value="30">30+ days</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-gray-400">Leads whose last call is older than this (never-called included).</p>
                        </div>

                        <div className="space-y-1">
                          <Label htmlFor="talk-under" className="text-xs text-muted-foreground">Longest conversation under</Label>
                          <div className="flex items-center gap-1 min-h-8">
                            {[10, 30, 60].map((s) => (
                              <Button
                                key={s}
                                type="button"
                                size="sm"
                                variant={filters.talkUnderSeconds === s ? 'default' : 'outline'}
                                className="h-auto self-stretch px-2.5 text-xs"
                                disabled={filters.neverCalled || filters.hasBeenCalled === false}
                                onClick={() => handleFilterChange('talkUnderSeconds', filters.talkUnderSeconds === s ? null : s)}
                              >
                                {s}s
                              </Button>
                            ))}
                            <Input
                              id="talk-under"
                              type="number"
                              placeholder="Custom"
                              className="h-auto self-stretch py-0 px-2 flex-1 min-w-0 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              value={filters.talkUnderSeconds ?? ''}
                              onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                handleFilterChange('talkUnderSeconds', n > 0 ? Math.min(n, 3600) : null);
                              }}
                              disabled={filters.neverCalled || filters.hasBeenCalled === false}
                              min={1}
                              max={3600}
                            />
                          </div>
                          <p className="text-[10px] text-gray-400">Called leads who never talked this many seconds. Voicemail doesn't count.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Visible Columns */}
                    <AccordionItem value="columns" className="border-none">
                      <AccordionTrigger className="text-sm font-medium py-2 hover:no-underline">
                        Columns
                      </AccordionTrigger>
                      <AccordionContent className="space-y-1.5 pb-3">
                        {LEAD_TABLE_COLUMNS.map((col) => (
                          <label
                            key={col.key}
                            className="flex items-center gap-2 cursor-pointer rounded px-1 py-0.5 hover:bg-muted/50"
                          >
                            <Checkbox
                              checked={filters.visibleColumns.includes(col.key)}
                              onCheckedChange={(checked) => {
                                const newColumns = checked
                                  ? [...filters.visibleColumns, col.key]
                                  : filters.visibleColumns.filter((c: string) => c !== col.key);
                                handleFilterChange('visibleColumns', newColumns);
                              }}
                            />
                            <span className="text-sm">{col.label}</span>
                          </label>
                        ))}
                        <CustomFieldColumnPicker
                          fields={availableCustomFields}
                          selected={filters.visibleColumns}
                          onToggle={(key, on) =>
                            handleFilterChange(
                              'visibleColumns',
                              on ? [...filters.visibleColumns, key] : filters.visibleColumns.filter((c: string) => c !== key),
                            )
                          }
                        />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sort By Dropdown + attached direction toggle */}
            {(() => {
              // Each group has a "default" value (used as the SelectItem value) and all its variants.
              // The current sortBy gets mapped to its group so the dropdown shows the right label.
              // Map the raw sortBy value to the group key that matches an actual <SelectItem>.
              // CRITICAL: must always return a string that exists as a SelectItem value below,
              // otherwise Radix Select gets confused and can trigger ref-composition loops.
              const VALID_GROUPS = new Set([
                'recentlyImported',
                'updatedAt',
                'lastContacted',
                'nameAsc',
                'age',
                'state',
                'custom',
              ]);
              // Multi-level custom sort encodes as "customMulti:<base64(JSON [{k,d,t}…])>"
              // — an ORDERED list of {key, dir, type} (first = primary, rest = tiebreakers).
              type Spec = { k: string; d: string; t: string };
              const isCustomMulti = sortBy?.startsWith('customMulti:') ?? false;
              const encodeMulti = (specs: Spec[]) =>
                'customMulti:' + btoa(encodeURIComponent(JSON.stringify(specs)));
              const decodeMulti = (sb: string): Spec[] => {
                try {
                  const arr = JSON.parse(decodeURIComponent(atob(sb.slice('customMulti:'.length))));
                  return Array.isArray(arr) ? arr : [];
                } catch { return []; }
              };
              const specs: Spec[] = isCustomMulti ? decodeMulti(sortBy as string) : [];
              const sortGroup = (() => {
                if (isCustomMulti) return 'custom';
                if (sortBy === 'recentlyImported' || sortBy === 'oldest') return 'recentlyImported';
                if (sortBy === 'updatedAt' || sortBy === 'updatedAtAsc') return 'updatedAt';
                if (sortBy === 'lastContacted' || sortBy === 'lastContactedAsc') return 'lastContacted';
                if (sortBy === 'nameAsc' || sortBy === 'nameDesc') return 'nameAsc';
                if (sortBy === 'age' || sortBy === 'ageDesc') return 'age';
                if (sortBy === 'state' || sortBy === 'stateDesc') return 'state';
                return VALID_GROUPS.has(sortBy || '') ? (sortBy as string) : 'lastContacted';
              })();
              const cur = isCustomMulti ? specs[0] : undefined;
              const effectiveAsc = isCustomMulti ? cur?.d === 'asc' : (sortBy === 'oldest' || sortBy?.endsWith('Asc') || sortBy === 'nameAsc' || sortBy === 'age' || sortBy === 'state');
              return (
                <div className="inline-flex items-stretch">
                  <Select
                    value={sortGroup}
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        // Default custom sort: a NUMERIC key field (Annual Premium) if there is one.
                        const first = keyFields.find((f) => f.type === 'numeric') ?? groupedFields[0];
                        if (first) onSortChange?.(encodeMulti([{ k: first.key, d: 'desc', t: first.type }]));
                      } else {
                        onSortChange?.(value);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[140px] h-9 text-sm rounded-r-none border-r-0 focus:ring-0 focus:ring-offset-0">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recentlyImported">Added</SelectItem>
                      <SelectItem value="updatedAt">Updated</SelectItem>
                      <SelectItem value="lastContacted">Last Call</SelectItem>
                      <SelectItem value="nameAsc">Name</SelectItem>
                      <SelectItem value="age">Age</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                      {availableCustomFields.length > 0 && (
                        <SelectItem value="custom">Custom field</SelectItem>
                      )}
                    </SelectContent>
                  </Select>

                  {/* Inline custom-field picker — only when a custom sort is active */}
                  {isCustomMulti && (
                    <Select
                      value={cur?.k || ''}
                      onValueChange={(key) => {
                        const f = availableCustomFields.find((x) => x.key === key);
                        onSortChange?.(encodeMulti([{ k: key, d: cur?.d || 'desc', t: f?.type || 'text' }]));
                      }}
                    >
                      <SelectTrigger className="w-[150px] h-9 text-sm rounded-none border-r-0 focus:ring-0 focus:ring-offset-0">
                        <SelectValue placeholder="Field…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(cur?.k && !pickerFields.some((f) => f.key === cur.k)
                          ? [...pickerFields, { key: cur.k, type: cur.t || 'text', isKeyField: false }]
                          : pickerFields
                        ).map((f) => (
                          <SelectItem key={f.key} value={f.key} className="text-sm">
                            {f.key}
                            <span className="text-gray-400 ml-1">({f.type === 'numeric' ? '#' : 'A→Z'})</span>
                          </SelectItem>
                        ))}
                        {keyFields.length > 0 && groupedFields.length > keyFields.length && (
                          <div
                            className="sticky bottom-0 z-10 -mx-1 -mb-1 mt-1 border-t bg-popover p-1 shadow-[0_-3px_6px_-2px_rgba(0,0,0,0.12)]"
                            // Same trick as the field search box: keep the Select from
                            // treating this as an item / typeahead keystroke.
                            onKeyDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-full justify-start px-2 text-xs"
                              onClick={() => setKeyFieldsOnly(!keyOnlyActive)}
                            >
                              {keyOnlyActive
                                ? `Show all fields (${groupedFields.length - keyFields.length} more)`
                                : 'Key fields only'}
                            </Button>
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0 rounded-l-none border-l"
                    title={effectiveAsc ? 'Ascending (click for descending)' : 'Descending (click for ascending)'}
                    onClick={() => {
                      if (isCustomMulti) {
                        if (cur) onSortChange?.(encodeMulti([{ ...cur, d: cur.d === 'asc' ? 'desc' : 'asc' }]));
                        return;
                      }
                      const flipMap: Record<string, string> = {
                        recentlyImported: 'oldest', oldest: 'recentlyImported',
                        updatedAt: 'updatedAtAsc', updatedAtAsc: 'updatedAt',
                        lastContacted: 'lastContactedAsc', lastContactedAsc: 'lastContacted',
                        nameAsc: 'nameDesc', nameDesc: 'nameAsc',
                        age: 'ageDesc', ageDesc: 'age',
                        state: 'stateDesc', stateDesc: 'state',
                      };
                      onSortChange?.(flipMap[sortBy] || sortBy);
                    }}
                  >
                    {effectiveAsc ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              );
            })()}

            {/* PolicyGuard — only visible when Sold & Approved selected */}
            {selectedStatus?.toLowerCase() === 'sold & approved' && onOpenPolicyGuard && (
              <Button
                onClick={onOpenPolicyGuard}
                variant="outline"
                size="sm"
                className="h-9 gap-1.5 relative border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <Shield className="h-3.5 w-3.5" />
                PolicyGuard
                {policyGuardAlertCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white">
                    {policyGuardAlertCount > 99 ? '99+' : policyGuardAlertCount}
                  </span>
                )}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Selection chip: same family as the PolicyGuard button beside it
                (primary tint, hairline border), reads as part of the header
                rather than a dark badge dropped on top of it. */}
            {selectedCount > 0 && (
              <div
                className="inline-flex h-7 items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-2.5 text-xs font-medium text-primary"
                data-testid="leads-selected-chip"
              >
                <CheckSquare className="h-3.5 w-3.5" />
                <span className="tabular-nums">{selectedCount.toLocaleString()} selected</span>
              </div>
            )}

            <div className="text-sm font-semibold text-gray-900 tabular-nums">
              {totalCount.toLocaleString()} {totalCount === 1 ? 'Lead' : 'Leads'} Found
            </div>
          </div>
        </div>

        {/* Active Filter Badges */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            {getActiveFilterBadges().map((badge, index) => (
              <Badge
                key={index}
                variant="outline"
                className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1 cursor-pointer hover:bg-blue-100"
                onClick={() => removeBadge(badge.key)}
              >
                {badge.label}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-xs h-6 px-2 text-muted-foreground hover:text-destructive"
            >
              Clear all
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterCustomTagInput({ onAddTag }: { onAddTag: (tag: string) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="mt-1.5 flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
      >
        <Plus className="h-3 w-3" />
        Filter by custom tag
      </button>
    );
  }

  return (
    <div className="mt-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Tag name..."
        className="h-6 w-28 rounded border border-gray-200 px-2 text-[10px] focus:outline-none focus:ring-1 focus:ring-primary"
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) {
            onAddTag(value.trim().toLowerCase().replace(/\s+/g, "_"));
            setValue("");
            setIsOpen(false);
          }
          if (e.key === "Escape") { setValue(""); setIsOpen(false); }
        }}
      />
      <button
        onClick={() => {
          if (value.trim()) {
            onAddTag(value.trim().toLowerCase().replace(/\s+/g, "_"));
          }
          setValue("");
          setIsOpen(false);
        }}
        className="text-[10px] text-primary hover:text-primary/80 font-medium"
      >
        Add
      </button>
    </div>
  );
}
